import Spin from 'antd/lib/spin';
import * as React from 'react';
import { FetchableEntityType, FetchableEntityTypeV2 } from '../../entityType';
import { MehOutlined } from '@ant-design/icons';
import Result from 'antd/lib/result';
import { antGrey } from '../styles/globalStyles';
import Button from 'antd/lib/button';
import {
    selectApp,
    selectIsDemoInstanceOrDemoModeEnabled,
    selectIsReleaseStepPreviouslyFetched,
    selectIsStepRunPreviouslyFetched,
    selectRelease,
    selectStepRunGraph,
    selectWorkflowEnabledSteps,
} from '../../selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { tryFetchEntity } from '../../actions/entities';
import {
    Nullable,
    ReleaseStepComponentProps,
    StepRunComponentProps,
    TemporalStepRunComponentProps,
} from '../../typescriptHelpers';
import { generateBackwardsCompatibleReleaseStepRunId } from '@/reducers/releaseStep';
import { useError, useGetEntityV2, useIsLoading } from '@/utils/hooks/helpers';
import { flatMap } from 'lodash';
import { useHistory } from 'react-router-dom';
import { createReleaseOverviewRoute } from '@/helpers/routeHelpers';

export const withReleaseStep = <P extends object>(
    WrappedComponent: React.ComponentType<
        P &
            ReleaseStepComponentProps &
            TemporalStepRunComponentProps & {
                stepRunComponentProps: Nullable<StepRunComponentProps>;
            }
    >,
) => {
    const ReleaseStepWrapper: React.FC<
        P &
            ReleaseStepComponentProps &
            TemporalStepRunComponentProps & {
                showLoader?: boolean;
                showError?: boolean;
                withTracking?: boolean;
            }
    > = (props) => {
        const { orgId, appId, releaseId, releaseStepType, stepRunId, showLoader = true, showError = true } = props;
        const [displayError, setDisplayError] = React.useState(false);
        const releaseStepId = generateBackwardsCompatibleReleaseStepRunId(releaseId, releaseStepType);

        const history = useHistory();

        const stepRunNodes = useSelector((state: RootState) => {
            const stepRunGraph = selectStepRunGraph(state, releaseId);

            return flatMap(stepRunGraph.stepGroups, (stepGroup) => {
                return flatMap(stepGroup.stepArchetypeGroups, (archetypeGroup) => {
                    return archetypeGroup.stepRunStatusDatum;
                });
            });
        });

        const matchingStepRunNode = stepRunNodes.find((stepRunNode) => {
            return stepRunNode.stepRunId === stepRunId;
        });

        const workflowRunId = useSelector((state: RootState) => {
            const release = selectRelease(state, releaseId);
            return release.workflowRunId;
        });

        const releaseVersion = useSelector((state: RootState) => {
            const release = selectRelease(state, releaseId);
            return release.version;
        });

        const workflowId = useSelector((state: RootState) => {
            const app = selectApp(state, appId);
            return app.workflowId;
        });

        const stepRunComponentProps: Nullable<StepRunComponentProps> =
            workflowRunId && workflowId && matchingStepRunNode ?
                {
                    orgId,
                    appId,
                    releaseId,
                    workflowId,
                    workflowRunId,
                    stepConfigId: matchingStepRunNode.stepConfigId,
                    stepRunId: matchingStepRunNode.stepRunId,
                }
            :   null;

        const hasStepRunProps = !!stepRunComponentProps;

        const [fetchStepRun, isLoadingStepRun, stepRunFetchError] = useGetEntityV2(
            FetchableEntityTypeV2.StepRun,
            matchingStepRunNode?.stepRunId,
        );

        const dispatch = useDispatch();
        const isLoadingReleaseStep = useIsLoading(FetchableEntityType.ReleaseStep, releaseStepId);
        const errorFetchingReleaseStep = useError(FetchableEntityType.ReleaseStep, releaseStepId);

        const isLoading = hasStepRunProps ? isLoadingStepRun : isLoadingReleaseStep;
        const errorFetching = hasStepRunProps ? stepRunFetchError : errorFetchingReleaseStep;

        const isReleaseStepPreviouslyFetched = useSelector((state: RootState) =>
            selectIsReleaseStepPreviouslyFetched(state, releaseId, releaseStepType),
        );

        const isStepRunPreviouslyFetched = useSelector((state: RootState) =>
            selectIsStepRunPreviouslyFetched(state, stepRunComponentProps?.stepRunId),
        );

        const isPreviouslyFetched = hasStepRunProps ? isStepRunPreviouslyFetched : isReleaseStepPreviouslyFetched;

        const isDemoMode = useSelector((state: RootState) =>
            selectIsDemoInstanceOrDemoModeEnabled(state, orgId, releaseId),
        );

        const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));

        const fetchReleaseStep = () => {
            setDisplayError(false);

            const isTryingToAccessDeprecatedStep = workflowEnabledSteps[releaseStepType];
            const isTryingToAccessUnknownStepRun = stepRunId && !matchingStepRunNode;

            if (isTryingToAccessDeprecatedStep || isTryingToAccessUnknownStepRun) {
                const releaseOverviewRoute = createReleaseOverviewRoute(orgId, appId, releaseVersion);
                history.replace(releaseOverviewRoute);
                return;
            }

            if (hasStepRunProps) {
                fetchStepRun(stepRunComponentProps);
            } else {
                dispatch(
                    tryFetchEntity(
                        FetchableEntityType.ReleaseStep,
                        { orgId, appId, releaseId, releaseStepType },
                        releaseStepId,
                    ),
                );
            }
        };

        React.useEffect(() => {
            if (!isDemoMode) {
                fetchReleaseStep();
            }
        }, [releaseStepId, stepRunId]);

        React.useEffect(() => {
            if (errorFetching && !isPreviouslyFetched) {
                setDisplayError(true);
            }
        }, [errorFetching]);

        return (
            <>
                {isLoading && !isPreviouslyFetched && showLoader && (
                    <div
                        style={{
                            display: 'flex',
                            flexGrow: 100,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Spin size="small" />
                    </div>
                )}
                {displayError && showError && (
                    <div
                        style={{
                            display: 'flex',
                            flexGrow: 1,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Result
                            icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                            subTitle="Error fetching release step"
                            extra={<Button onClick={fetchReleaseStep}>Try again</Button>}
                        />
                    </div>
                )}
                {isPreviouslyFetched && (
                    <WrappedComponent
                        {...props}
                        stepRunComponentProps={stepRunComponentProps}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        releaseStepType={releaseStepType}
                    />
                )}
            </>
        );
    };

    // Set display name for easier debugging
    const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    ReleaseStepWrapper.displayName = `withReleaseStep(${wrappedComponentName})`;

    return ReleaseStepWrapper;
};
